import { type ChangeEvent, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { Info, Loader2, Lock, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { FormError } from '@/components/ui/form-field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Modal } from '@/components/ui/modal';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import { SimpleSelect } from '@/components/ui/simple-select';

import RolesAndPermissions from '../roles-and-permissions/RolesAndPermissions';
import {
  useAddTeamMemberMutation,
  useUpdateTeamMemberMutation,
  useGetRolesQuery,
  getRoleSelectOptions,
  assignRoles,
} from '../services/teamApi';
import { dismissAlert, showAlert } from '../../../slice/AlertSlice';
import { Variants } from '@/types/common';
import type { RadioOption } from '../../../types/common';
import { formatPhoneNumberInField } from '../../../util/formatting';
import { getEntitySubEntitiesDropdown } from '../../../services/current-context';
import type { TeamMemberFormValues } from '../../../types/team.types';
import { useMediaQuery } from '../../../hooks/useMediaQuery';

const schema = yup.object().shape({
  firstName: yup
    .string()
    .required('Please enter first name')
    .matches(/^[a-zA-Z.,' -]*$/, 'Please enter letters, spaces, hyphen, period, comma and apostrophe only'),
  lastName: yup
    .string()
    .required('Please enter last name')
    .matches(/^[a-zA-Z.,' -]*$/, 'Please enter letters, spaces, hyphen, period, comma and apostrophe only'),
  email: yup.string().matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Invalid email').required('Please enter email'),
  mobile: yup
    .string()
    .required('Please enter phone number')
    .test('is-valid-phone', 'Please enter a valid 10-digit phone number', (value) => {
      if (!value) return false;
      const digits = value.replace(/\D/g, '');
      return (digits.length === 10 && !digits.startsWith('1')) || (digits.length === 11 && digits.startsWith('1'));
    }),
  role: yup.string().required('Please select a role'),
  profileStatus: yup.string(),
});

const PROFILE_STATUS_OPTIONS: RadioOption[] = [
  { label: 'Active', id: 'Active', value: 'Active' },
  { label: 'Inactive', id: 'Inactive', value: 'Inactive' },
];

const EMPTY_VALUES: TeamMemberFormValues = {
  firstName: '', lastName: '', email: '', mobile: '', role: '',
};

export interface MemberInitialData extends TeamMemberFormValues {
  entity?: string;
}

interface ManageMemberDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pass null/undefined for add mode; pass an object for edit mode. */
  member: { id: string; data: MemberInitialData } | null;
}

const ManageMemberDrawer = ({ open, onOpenChange, member }: ManageMemberDrawerProps) => {
  const dispatch = useDispatch();
  const { data: roles = [] } = useGetRolesQuery();
  const [addMember] = useAddTeamMemberMutation();
  const [updateMember] = useUpdateTeamMemberMutation();

  const [isLoading, setIsLoading] = useState(false);
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [showConfirmDiscard, setShowConfirmDiscard] = useState(false);
  const dirtyRef = useRef(false);

  const isEdit = Boolean(member);
  const entityOptions = getEntitySubEntitiesDropdown();
  const defaultEntity = entityOptions[0]?.value ?? '';

  // Derive form values directly from props so the first render already has the
  // correct values. A previous useState+useEffect dance left form fields stale
  // on the first render, which put Radix's RadioGroup into uncontrolled mode
  // and stuck the Profile Status selection regardless of later updates.
  const iniValue: TeamMemberFormValues = useMemo(
    () =>
      member
        ? { ...member.data, entity: member.data.entity || defaultEntity }
        : { ...EMPTY_VALUES, entity: defaultEntity },
    [member, defaultEntity],
  );
  const backupEmail = member?.data.email ?? '';
  const initialRoleValue = useMemo(
    () =>
      member
        ? getRoleSelectOptions(roles).find((o) => o.value === member.data.role)
        : undefined,
    [member, roles],
  );

  const handleSubmit = async (
    values: TeamMemberFormValues,
    setFieldError: (field: string, msg: string) => void,
    resetForm: () => void,
  ) => {
    setIsLoading(true);
    const trimmed = {
      ...values,
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email?.trim(),
      mobile: values.mobile.replace(/\D/g, ''),
    };
    try {
      if (member) {
        const body = {
          userId: member.id,
          firstName: trimmed.firstName,
          lastName: trimmed.lastName,
          ...(backupEmail !== trimmed.email ? { email: trimmed.email } : {}),
          mobile: trimmed.mobile,
          // Scope the update to the member's OWN entity, not the admin's current
          // context. When the hierarchy list is viewed from a parent entity,
          // getCurrentContext() returns the parent — sending that would reassign a
          // child-entity member to the parent on a role change (DV-2301). The
          // member's entity is sent both top-level (drives updateUser's group
          // resolution on the direct edit path) and in roles[].entityName (the
          // backend's canonical entity-for-role source).
          entity: values.entity,
          roles: assignRoles(trimmed.role, values.entity),
          isActive: trimmed.profileStatus !== 'Inactive',
          profileStatus: trimmed.profileStatus,
        };
        await updateMember(body).unwrap();
      } else {
        await addMember({
          firstName: trimmed.firstName,
          lastName: trimmed.lastName,
          email: trimmed.email,
          mobile: trimmed.mobile,
          roles: assignRoles(trimmed.role),
        }).unwrap();
      }
      dispatch(showAlert({
        type: Variants.SUCCESS,
        message: member ? 'Changes saved successfully.' : 'User added successfully.',
      }));
      resetForm();
      dirtyRef.current = false;
      onOpenChange(false);
    } catch (error: unknown) {
      const apiErr = error as { data?: { code?: string } };
      if (apiErr?.data?.code?.includes('USER_ALREADY_EXISTS')) {
        setFieldError('email', 'Email already exists. Please enter a different email.');
      } else {
        dispatch(showAlert({ type: Variants.ERROR, apiResponse: error }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Single funnel for all close paths (Cancel button, X, Esc, overlay click).
  // If the user has unsaved changes, prompt before discarding.
  const requestClose = () => {
    if (dirtyRef.current) {
      setShowConfirmDiscard(true);
    } else {
      dispatch(dismissAlert());
      onOpenChange(false);
    }
  };

  const discardAndClose = () => {
    setShowConfirmDiscard(false);
    dirtyRef.current = false;
    dispatch(dismissAlert());
    onOpenChange(false);
  };

  const FORM_ID = 'manage-member-form';
  const isMobile = useMediaQuery('(max-width: 639px)');

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) requestClose();
      }}
    >
      <SheetContent
        side={isMobile ? 'bottom' : 'right'}
        className={
          isMobile
            ? 'flex h-[92vh] w-full flex-col gap-0 rounded-t-xl p-0 [&>button]:hidden'
            : 'flex w-full flex-col gap-0 p-0 sm:max-w-[540px] [&>button]:hidden'
        }
      >
        <Formik<TeamMemberFormValues>
          validationSchema={schema}
          initialValues={iniValue}
          validateOnBlur
          validateOnChange={false}
          onSubmit={(values, { setFieldError, resetForm }) =>
            handleSubmit(values, setFieldError, resetForm)
          }
          enableReinitialize
        >
          {({ handleSubmit: onSubmit, handleChange, values, touched, errors, setFieldValue, dirty }) => {
            dirtyRef.current = dirty;
            return (
              <>
              {/* Header */}
              <div className="flex items-start justify-between gap-3 border-b border-border px-6 py-5">
                <div className="min-w-0">
                  <SheetTitle className="text-xl">
                    {isEdit ? 'Edit Team Member' : 'Add Team Member'}
                  </SheetTitle>
                  <SheetDescription className="mt-1">
                    {isEdit
                      ? "Update this team member's contact details and role."
                      : "Invite a new member to your team. They'll receive an email to set up their account."}
                  </SheetDescription>
                </div>
                <SheetClose asChild>
                  <button
                    type="button"
                    aria-label="Close"
                    // Bespoke button, not the Button primitive, so Unit 2's
                    // touch floors don't reach it: 32x32 is under the 44px
                    // floor on touch. The glyph stays 16px; only the hit area
                    // grows below lg.
                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/25 max-lg:h-11 max-lg:w-11"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </SheetClose>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto">
                <Form id={FORM_ID} noValidate onSubmit={onSubmit} className="space-y-5 p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="first-name" required>First Name</Label>
                      <Input id="first-name" name="firstName" maxLength={35} value={values.firstName} onChange={handleChange} className={touched.firstName && errors.firstName ? 'border-destructive' : ''} data-testid="first-name_id" />
                      <FormError message={touched.firstName ? errors.firstName : undefined} />
                    </div>
                    <div>
                      <Label htmlFor="last-name" required>Last Name</Label>
                      <Input id="last-name" name="lastName" maxLength={35} value={values.lastName} onChange={handleChange} className={touched.lastName && errors.lastName ? 'border-destructive' : ''} data-testid="last-name_id" />
                      <FormError message={touched.lastName ? errors.lastName : undefined} />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email" required>Email</Label>
                      <Input id="email" name="email" disabled={isEdit} maxLength={320} value={values.email} onChange={handleChange} className={touched.email && errors.email ? 'border-destructive' : ''} data-testid="email_id" />
                      <FormError message={touched.email ? errors.email : undefined} />
                    </div>
                    <div>
                      <Label htmlFor="mobileNumber" required>Phone</Label>
                      <Input id="mobileNumber" name="mobile" placeholder="(555) 123-4567" value={values.mobile} onChange={(e: ChangeEvent<HTMLInputElement>) => setFieldValue('mobile', formatPhoneNumberInField(e.target.value))} className={touched.mobile && errors.mobile ? 'border-destructive' : ''} data-testid="mobileNumber_id" />
                      <FormError message={touched.mobile ? errors.mobile : undefined} />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="entity-display" required>Entity</Label>
                      <div
                        id="entity-display"
                        aria-readonly="true"
                        data-testid="entity_id"
                        className="flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-muted px-3 text-sm text-foreground"
                      >
                        <span className="truncate">{values.entity || '—'}</span>
                        <Lock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
                      </div>
                    </div>
                    <SimpleSelect
                      options={getRoleSelectOptions(roles)}
                      name="role" md="12" controlId="role" ariaDescribedBy="role-select"
                      placeholder="Select User Role"
                      label={
                        <span className="inline-flex items-center">
                          Role
                          <span className="text-destructive ml-0.5">*</span>
                          <button
                            type="button"
                            onClick={() => setShowRolesModal(true)}
                            aria-label="Roles & Permissions"
                            // Opens the Roles & Permissions modal -- a real
                            // tap action, not a hover tooltip, so it needs a
                            // real touch target. 14x14 is the glyph; below lg
                            // the button grows to the 44px floor and the
                            // negative margins keep the label's line box from
                            // growing with it.
                            className="ml-1.5 inline-flex h-3.5 w-3.5 items-center justify-center text-muted-foreground hover:text-foreground max-lg:-my-4 max-lg:h-11 max-lg:w-11"
                          >
                            <Info className="h-3.5 w-3.5" />
                          </button>
                        </span>
                      }
                      value={initialRoleValue}
                      errors={touched.role ? errors.role : undefined}
                      setFieldValue={setFieldValue} field="role"
                    />
                  </div>

                  {isEdit && (
                    <div className="border-t border-border pt-4">
                      <Label>Profile Status</Label>
                      <RadioGroup
                        value={values.profileStatus}
                        onValueChange={(v) => setFieldValue('profileStatus', v)}
                        className="flex gap-6"
                      >
                        {PROFILE_STATUS_OPTIONS.map((opt) => (
                          <div key={opt.value} className="flex items-center gap-2">
                            <RadioGroupItem value={opt.value} id={opt.id ?? opt.value} />
                            <Label htmlFor={opt.id ?? opt.value} className="font-normal mb-0">{opt.label}</Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>
                  )}
                </Form>
              </div>

              {/* Footer */}
              <div className="flex shrink-0 justify-end gap-2 border-t border-border bg-card px-6 py-4">
                <Button type="button" variant="outline" onClick={requestClose}>Cancel</Button>
                <Button type="submit" form={FORM_ID} disabled={isLoading}>
                  {isLoading && <Loader2 className="animate-spin" />}
                  {isEdit ? 'Save Changes' : 'Send Invite'}
                </Button>
              </div>
            </>
            );
          }}
        </Formik>
      </SheetContent>

      <RolesAndPermissions show={showRolesModal} onDismiss={() => setShowRolesModal(false)} />

      <Modal
        open={showConfirmDiscard}
        onOpenChange={setShowConfirmDiscard}
        title="Discard Changes?"
        description="You have unsaved changes. Closing this form will discard them."
        size="sm"
        footer={
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowConfirmDiscard(false)}
              autoFocus
            >
              Keep Editing
            </Button>
            <Button type="button" variant="destructive" onClick={discardAndClose}>
              Discard
            </Button>
          </>
        }
      />
    </Sheet>
  );
};

export default ManageMemberDrawer;
